import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { ModuleRenderer, NavigationModule, FooterModule } from "@/pages/ModularPage";
import CompareTray from "./CompareTray";
import useStickyTopOffset from "@/hooks/useStickyTopOffset";
import useAdminEditMode from "@/hooks/useAdminEditMode";
import { EditModeContext } from "@/contexts/EditModeContext";
import EditModeBanner from "./EditModeBanner";
import EditableModuleOverlay from "./EditableModuleOverlay";
import InstanceQuickEditDialog from "./cms/InstanceQuickEditDialog";

const API = getApiUrl();

// De-dupes the handful of near-simultaneous `/page-overlays/render` calls a
// single page view fires (React StrictMode's double-invoked effects, plus
// more than one <PageShell> mounting during a route's loading→loaded
// transition) down to one real request per path. Short-lived — this isn't
// a real cache, overlays can change per route and should stay fresh.
const _overlaysInFlight = {};
const fetchOverlaysDeduped = async (pathname) => {
  if (_overlaysInFlight[pathname]) return _overlaysInFlight[pathname];
  const promise = axios.get(`${API}/page-overlays/render`, { params: { path: pathname } })
    .finally(() => { delete _overlaysInFlight[pathname]; });
  _overlaysInFlight[pathname] = promise;
  return promise;
};

/**
 * PageShell wraps any page with per-page "overlays" — up to 2 module instances
 * shown BEFORE the page's main content and up to 2 AFTER — keyed by the URL
 * path. There is NO global header / navigation / footer anymore: admins add
 * any of those as overlay instances per page when they want them.
 *
 * Fixed routes that don't live in the Modular Page Builder (e.g. `/checkout`,
 * `/payment/success`) can opt into the standard site chrome — Header +
 * Navigation + Footer — by passing `chrome`. In that mode we look up the
 * *default* module instance for each type (`is_default: true` in
 * db.module_instances) and render them with that instance's stored config,
 * so the chrome stays in lock-step with every modular page on the site.
 */
const CHROME_TYPES = ["navigation", "footer"];
const CHROME_COMPONENTS = { navigation: NavigationModule, footer: FooterModule };

export default function PageShell({ children, chrome = false }) {
  const [overlays, setOverlays] = useState({ pre: [], post: [] });
  const [chromeInstances, setChromeInstances] = useState({ navigation: null, footer: null });
  const location = useLocation();
  const { bannerVisible, editMode, setEditParam } = useAdminEditMode();
  const [editingInstanceId, setEditingInstanceId] = useState(null);
  const [allPages, setAllPages] = useState({ modular: [], preset: [], custom: [] });
  // Publishes the `--slh-sticky-top-offset` CSS variable on <html> based on
  // the cumulative height of any `[data-slh-top-banner]` elements currently
  // mounted (admin bar, deployment banner, in-app system notifications…).
  // The sticky header CTA reads this variable so it always sits below.
  useStickyTopOffset();

  const fetchOverlays = async () => {
    try {
      const r = await fetchOverlaysDeduped(location.pathname);
      setOverlays({ pre: r.data?.pre || [], post: r.data?.post || [] });
    } catch {
      setOverlays({ pre: [], post: [] });
    }
  };

  const fetchChromeInstances = async () => {
    const results = await Promise.all(
      CHROME_TYPES.map((t) =>
        axios
          .get(`${API}/module-instances/default/${t}`)
          .then((r) => r.data)
          .catch(() => null),
      ),
    );
    setChromeInstances({ navigation: results[0], footer: results[1] });
  };

  // Re-fetch overlays whenever the route changes.
  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await fetchOverlays(); })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Load the default module instances for the chrome ONCE (they rarely
  // change). Each type falls back to a null instance and the corresponding
  // module renders nothing rather than swapping to a legacy fallback.
  useEffect(() => {
    if (!chrome) return;
    let cancelled = false;
    (async () => { if (!cancelled) await fetchChromeInstances(); })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chrome]);

  // Edit Mode: lazily load the page picker list (only Master Admins editing
  // a page need it) and re-pull overlay/chrome content after a save so the
  // page reflects the just-edited instance without a manual refresh.
  useEffect(() => {
    if (!editMode) return;
    let cancelled = false;
    (async () => {
      try {
        const [modularRes, presetRes, customRes] = await Promise.all([
          axios.get(`${API}/admin/page-compositions`, { withCredentials: true }),
          axios.get(`${API}/navigation`, { withCredentials: true }),
          axios.get(`${API}/admin/custom-static-pages`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setAllPages({
          modular: modularRes.data.compositions || [],
          preset: (presetRes.data || []).filter((p) => p.path && p.path !== "#"),
          custom: customRes.data.pages || [],
        });
      } catch {
        /* page picker lists are a nice-to-have inside the editor, not critical */
      }
    })();
    return () => { cancelled = true; };
  }, [editMode]);

  useEffect(() => {
    const onInstanceUpdated = () => { fetchOverlays(); if (chrome) fetchChromeInstances(); };
    window.addEventListener("slh:module-instance-updated", onInstanceUpdated);
    return () => window.removeEventListener("slh:module-instance-updated", onInstanceUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, chrome]);

  // If the Master Admin session ends while a quick-edit dialog happens to
  // be open, close it too — Edit Mode going off should leave no trace of
  // itself on screen.
  useEffect(() => {
    if (!editMode) setEditingInstanceId(null);
  }, [editMode]);

  const renderInstance = (inst, idx, side) => (
    <EditableModuleOverlay key={`${side}-${inst.instance_id || idx}`} instanceId={inst.instance_id} moduleType={inst.module_type}>
      <ModuleRenderer
        module={{
          module_type: inst.module_type,
          instance_id: inst.instance_id,
          config: inst.config,
        }}
        moduleDetail={{
          type: inst.module_type,
          name: inst.name,
          instance_id: inst.instance_id,
          config: inst.config,
        }}
      />
    </EditableModuleOverlay>
  );

  const renderChrome = (type) => {
    const inst = chromeInstances[type];
    if (!inst) return null;
    const Comp = CHROME_COMPONENTS[type];
    return (
      <EditableModuleOverlay key={inst.instance_id} instanceId={inst.instance_id} moduleType={type}>
        <Comp
          config={inst.config || {}}
          data-instance-id={inst.instance_id}
        />
      </EditableModuleOverlay>
    );
  };

  return (
    <EditModeContext.Provider value={{ editMode, openEditor: setEditingInstanceId }}>
      <div className="min-h-screen flex flex-col" data-public-site>
        <EditModeBanner isAdmin={bannerVisible} editMode={editMode} onToggle={setEditParam} dialogOpen={!!editingInstanceId} />
        {chrome && (
          <div data-testid="pageshell-chrome-top">
            {renderChrome("navigation")}
          </div>
        )}
        <main className="flex-1">
          {overlays.pre.map((inst, i) => renderInstance(inst, i, "pre"))}
          {children}
          {overlays.post.map((inst, i) => renderInstance(inst, i, "post"))}
        </main>
        {chrome && (
          <div data-testid="pageshell-chrome-bottom">
            {renderChrome("footer")}
          </div>
        )}
        {/* Floating compare-cart tray — visible on any public page while the
            visitor queues 1-3 properties to compare. Self-hides on /compare
            and on admin routes. */}
        <CompareTray />
        <InstanceQuickEditDialog
          open={!!editingInstanceId}
          onOpenChange={(v) => !v && setEditingInstanceId(null)}
          instanceId={editingInstanceId}
          allPages={allPages}
          onSaved={() => window.dispatchEvent(new CustomEvent("slh:module-instance-updated"))}
        />
      </div>
    </EditModeContext.Provider>
  );
}
